<template>
    <b-overlay :show="loading">
        <template #overlay>
            <div class="text-center">
                <b-spinner label="Loading..."></b-spinner>
                <div class="mt-2 mb-2">
                    <template v-if="apply_loading">
                        {{ $t('평가를 적용 중입니다...') }}    
                    </template>
                    <template v-if="aiEvDataFetchLoading || fetch_loading">
                        {{ $t('AI 평가 데이터를 불러오는 중입니다...') }}
                    </template>
                </div>
            </div>
        </template>
        <div class="ai-ev-container">
            <div class="ai-ev-actions">
                <div class="versions">
                    <div class="label">
                        {{ $t('평가 버전') }}
                    </div>
                    <div 
                         v-b-tooltip.hover
                         class="ai-refresh-btn"
                         :title="$t('현재 문항의 AI 평가 데이터 \n새로 불러오기')"
                         style="margin-left: 4px; margin-right: 2px"
                         @click="fetchSubAiEvData"
                    >
                        <fa icon="sync-alt"></fa>
                    </div>
                    <b-form-select v-model="current_ai_ev_version" class="version-select"
                                   size="sm"
                    >
                        <b-form-select-option v-if="no_ai_audit"
                                              disabled
                                                :value="null"
                        >
                            {{ $t('No Data.')}}
                        </b-form-select-option>
                        <b-form-select-option v-for="(data) in arr_ai_ev_data"
                                              :key="`ai-ev-version-${data.id}`"
                                              :value="data.id">
                            {{ data.version }}
                        </b-form-select-option>
                    </b-form-select>
                    <div>
                        <b-button size="sm" variant="dark-border"
                                  :disabled="loading || no_ai_audit"
                                  @click="openDetailAIModal"
                        >
                            상세
                        </b-button>
                    </div>
                </div>

                <div class="d-flex flex-column align-items-end">
                    <div class="d-flex">
                        <b-form-select v-model="judgmentModel" size="sm" aria-label="판정 모델"
                                       :disabled="loading || judgmentModelsLoading" class="mr-2" style="width: 220px">
                            <b-form-select-option :value="null">{{ judgmentModelsLoading ? '판정 모델 목록을 불러오는 중...' : defaultJudgmentModelLabel }}</b-form-select-option>
                            <b-form-select-option-group v-for="group in judgmentModelGroups" :key="group.provider" :label="group.label">
                                <b-form-select-option v-for="model in group.models" :key="model.id" :value="model.id">{{ judgmentModelLabel(model) }}</b-form-select-option>
                            </b-form-select-option-group>
                        </b-form-select>
                        <b-dropdown size="sm" class="mr-2" :disabled="loading || !can_request_ai_assessment"
                                    :text="$t('AI 평가 요청')">
                            <b-dropdown-item @click="requestAIAssessment('immediate')">즉시</b-dropdown-item>
                            <b-dropdown-item v-if="!judgmentModelForcesImmediate" @click="requestAIAssessment('batch')">배치 (최대 24시간)</b-dropdown-item>
                        </b-dropdown>
                        <b-button size="sm" variant="primary"
                                  :disabled="loading || current_ai_ev_data?.status !== 'completed'"
                                  @click="copySub"
                        >
                            {{ $t('문항 AI 평가 적용') }}
                        </b-button>
                    </div>
                    <small v-if="selectedJudgmentModelNote" class="text-muted mt-1">{{ selectedJudgmentModelNote }}</small>
                    <small v-if="judgmentModelForcesImmediate" class="text-warning mt-1">Gemini 모델은 즉시 실행만 지원되며, 배치 할인이 적용되지 않습니다.</small>
                    <small v-if="judgmentModelsError" class="text-warning mt-1">판정 모델 목록을 불러오지 못했습니다. 기본값으로 요청할 수 있습니다.</small>
                </div>


            </div>
            <b-row no-gutters class="mx-0 border-bottom border-color-gray-300 pb-1 mb-2 gray-550">
                <b-col class="col-9 f-90"> {{ $t('평가기준') }}</b-col>
                <b-col class="col-2 text-center f-90"> 수준</b-col>
                <b-col class="col-1 text-center f-90"> 항목적용</b-col>
            </b-row>
            <b-row v-for="(item) in ev_scoring_list" :key="`subs-${sub_code}-ev-${item.ev_info?.ev_id}`"
                   no-gutters
                   :class="['d-flex justify-content-bewteen align-items-center mx-0 mb-3', check_diff_ev(item) ]"
            >
                <b-col class="col-9" style="margin-top: 12px">
                    <b-form-checkbox v-model="get_ai_ev_item(item.ev_info?.ev_id).is_satisfied"
                                     class="f-95"
                                     style="margin-left: 12px"
                                     disabled
                    >
                        {{ item.ev_info.name }}
                    </b-form-checkbox>
                </b-col>
                <b-col cols="2" :class="[{ 'red5 f-90': item.ev_result.not_applicable }, 'text-center']">
                    <b-badge>
                        {{ itemEvCode(item) }}
                    </b-badge>
                </b-col>
                <b-col class="col-1 text-center">
                    <div class="d-flex flex-column align-items-center">
                        <b-button v-b-tooltip.hover="$t('AI 평가 복사')"
                                  class="py-1" variant="icon-dark"
                                  :disabled="loading || current_ai_ev_data?.status !== 'completed'"
                                  @click="copyItem(item)"
                        >
                            <fa icon="file-import"></fa>
                        </b-button>
                        <b-button v-if="canUseQna(item.ev_info?.ev_id)"
                                  v-b-tooltip.hover="$t('AI 평가 Q&A')"
                                  class="py-1" variant="icon-dark"
                                  :disabled="loading"
                                  @click="openQnaModal(item)"
                        >
                            <fa :icon="['fas', 'comments']"></fa>
                        </b-button>
                    </div>
                </b-col>
                <b-col class="col-12 d-flex" style="margin-bottom: 12px">
                    <template v-if="get_ai_ev_item(item.ev_info?.ev_id).converted_reason">
                        <div class="audit-container">
                            <b-form-select
                                v-model="get_ai_ev_item(item.ev_info?.ev_id).converted_reason.audit_category_code"
                                class="audit-category"
                                disabled
                            >
                                <b-form-select-option v-for="category in audit_category"
                                                      :key="category.code"
                                                      :value="category.code"
                                                      :disabled="category.code == null"
                                >
                                    {{ get_audit_category_name(category, $i18n) }}
                                </b-form-select-option>
                            </b-form-select>
                            <b-form-textarea
                                v-model="get_ai_ev_item(item.ev_info?.ev_id).converted_reason.audit_comment"
                                placeholder="평가 항목별 의견"
                                :class="['f-95 audit-comment', 'cursor']"
                                readonly
                                :rows="6"
                                :max-rows="6"
                                @click="copyAuditBasis(get_ai_ev_item(item.ev_info?.ev_id).converted_reason.audit_comment)"
                            >
                            </b-form-textarea>
                        </div>
                    </template>
                    <div v-else class="no-ai-audit-data-container">
                        <div class="no-ai-audit_title-wrapper">
                            <fa icon="robot" class="blue7" style="margin-right: 6px"/>
                            <div class="no-ai-audit_title">
                                {{ no_ai_audit_title(item.info?.ev_id) }}
                            </div>
                        </div>
                        <div class="no-ai-audit_desc">{{ no_ai_audit_desc(item.info?.ev_id) }}</div>
                    </div>
                    
                </b-col>
            </b-row>
        </div>

        <AIQnaChatModal :modal-id="qnaModalId"
                        :ai-audit-id="current_ai_ev_data?.id"
                        :ev-id="selectedQnaEvId"
                        :ev-name="selectedQnaEvName"
                        :ai-ev-item="selectedQnaEvItem"
                        @revision-applied="onRevisionApplied"
        />
    </b-overlay>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import dayjs from 'dayjs';
import {aiAuditLoadingText} from '@shared/components/admin/pages/Management/AIAssessment/data';
import {getDate} from '@shared/components/admin/pages/Management/common-data';
import AIQnaChatModal from '@shared/components/admin/pages/Management/AIAssessment/_SubComponent/AIQnaChatModal.vue';
import judgmentModelsMixin from '@/mixins/judgmentModels';

export default {
    name: 'AIEvaluationComponentV2',
    components: {AIQnaChatModal},
    mixins: [judgmentModelsMixin],
    props: {
        sub_code: {
            type: String
        },
        sub_id: {
            type: [Number,null],
            default: null
        },
        sub_label: {
            type: [String,null],
            default: null
        },
        ev_scoring_list: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            current_ai_ev_version: null,
            version_list: [],
            detail_ai_assessment_data: null,
            fetch_loading: false,
            apply_loading: false,
            selectedQnaEvId: null,
            selectedQnaEvName: '',
        };
    },
    computed: {
        ...mapState(['is_admin']),
        ...mapState('evaluations', ['audit_category']),
        ...mapState('ai_assessment', ['aiEvData', 'aiEvDataFetchLoading']),
        ...mapState('evaluations', ['audit_category', 'ev_code_list', 'map_ev_lv_variant']),
        ...mapGetters('evaluations', ['get_audit_category_name','mapEvCodeLv' ]),
        get_date() {
            return (date) => { return getDate(date); };
        },
        sub_ai_ev_data(){
            return this.aiEvData?.[this.sub_id] || null;
        },
        no_ai_audit(){
            return this.sub_ai_ev_data === null;
        },
        can_request_ai_assessment(){
            const lastest_status = this.arr_ai_ev_data?.[0]?.status ?? null;
            const last_requested_at = this.arr_ai_ev_data?.[0]?.updated_at ?? this.arr_ai_ev_data?.[0]?.created_at ?? undefined;
            
            if(lastest_status === 'processing'|| lastest_status === 'requested' || lastest_status === 'awaiting_ai') {
                return dayjs(last_requested_at).diff(dayjs(), 'minute') > 5;
            }
            return lastest_status === 'completed'
                || lastest_status === 'partial_failed'
                || lastest_status === 'failed'
                || this.no_ai_audit;
        },
        arr_ai_ev_data() {
            return Object.values(this.sub_ai_ev_data || {}).sort((a, b) => {
                return dayjs(b.created_at).diff(dayjs(a.created_at));
            });
        },
        current_ai_ev_data(){
            return this.sub_ai_ev_data?.[this.current_ai_ev_version] ?? null;
        },
        check_diff_ev(){
            return (ev_item) => {
                if(this.current_ai_ev_data?.status !== 'completed') return {};
                return {
                    'ev-diff-bg': this.current_ai_ev_data?.valid_map?.[ev_item.ev_info.ev_id]?.is_satisfied !== ev_item.ev_result.satisfied,
                    'ev-diff-error': !this.current_ai_ev_data?.valid_map?.[ev_item.ev_info.ev_id]?.is_satisfied && ev_item.ev_result.satisfied,
                };
            };
        },
        get_ai_ev_item() {
            return (ev_id) => {
                return this.current_ai_ev_data?.valid_map?.[ev_id] ?? {};
            };
        },
        qnaModalId() {
            // 이 컴포넌트는 문항(sub)별로 여러 인스턴스가 렌더되므로 sub_code로 유일화
            return `ai-qna-modal-eval-component-${this.sub_code}`;
        },
        canUseQna() {
            // AI 판정(reason)이 존재하는 ev에만 노출 (422 사전 차단) + is_admin ≥ 3 게이팅.
            // valid_map에는 reason == null(AI 미호출) 항목도 들어오므로 reason까지 확인.
            return (ev_id) => {
                return this.is_admin >= 3 && this.current_ai_ev_data?.valid_map?.[ev_id]?.reason != null;
            };
        },
        selectedQnaEvItem() {
            if (this.selectedQnaEvId == null) return null;
            return this.current_ai_ev_data?.valid_map?.[this.selectedQnaEvId] ?? null;
        },
        itemEvCode() {
            return (item) => {
                const ev_code = item.ev_result.pos_ev_code;
                const target = this.ev_code_list.find(e => e.code === ev_code);
                return target?.description || '-';
            };
        },
        is_audited() {
            return (item) => {
                return item.ev_result.judged_by === 'audit';
            };
        },
        loading(){
            return this.aiEvDataFetchLoading || this.apply_loading || this.fetch_loading;
        },
        no_ai_audit_title(){
            return (ev_id) => {
                if(this.no_ai_audit)
                    return this.$t('AI 평가 데이터가 없습니다.');
                const current_status = this.current_ai_ev_data?.status ?? null;
                if(current_status === 'completed' && this.get_ai_ev_item(ev_id).converted_reason == null)
                    return this.$t('AI 평가 내용이 없습니다.');
                return aiAuditLoadingText(current_status, this.$i18n);

            };
            
        },
        no_ai_audit_desc(){
            return (ev_id) => {
                if(this.no_ai_audit)
                    return this.$t('상단의 "AI 평가 요청" 버튼을 클릭하여 AI 평가를 요청해주세요.');
                const current_status = this.current_ai_ev_data?.status ?? null;
                if(current_status === 'requested' || current_status === 'processing' || current_status === 'awaiting_ai' || current_status === 'awaiting_batch' || current_status === 'retrying')
                    return `${this.$t('AI 평가가 완료될 때까지 기다려주세요.')}\n${this.$t('평가요청 일시')}: ${this.get_date(this.current_ai_ev_data?.created_at)}`;
                if(current_status === 'failed' || current_status === 'cancelled')
                    return this.$t('상단의 "AI 평가 요청" 버튼을 클릭하여 AI 평가를 다시 요청해주세요.');
                if(this.get_ai_ev_item(ev_id).converted_reason == null)
                    return this.$t('추가적인 내용이 필요하면 상단의 "AI 평가 요청" 버튼을 클릭하여 AI 평가를 다시 요청해주세요.');
                return null;
            };
        }
    },
    watch: {
        arr_ai_ev_data: {
            deep: true,
            handler(){
                this.current_ai_ev_version = this.arr_ai_ev_data?.[0]?.id ?? null;
            }
        }
    },
    mounted() {
        this.current_ai_ev_version = this.arr_ai_ev_data?.[0]?.id ?? null;
    },
    methods: {
        ...mapActions('ai_assessment', ['fetchAiEvData', 'setSubAiEvData']),
        async fetchSubAiEvData(preferredAiAuditId = null) {
            const canPreserveSelection = ['number', 'string'].includes(typeof preferredAiAuditId);
            const selectedAiAuditId = canPreserveSelection ? preferredAiAuditId : null;
            try {
                this.fetch_loading = true;
                
                const aiEvData = await this.fetchAiEvData({sub_id: this.sub_id, loadingControl: false });
                if (aiEvData == null) return;
                await this.setSubAiEvData({
                    sub_id: this.sub_id,
                    aiEvData: aiEvData,
                });
                await this.$nextTick();
                const selectedAudit = selectedAiAuditId == null
                    ? null
                    : this.arr_ai_ev_data.find(({id}) => String(id) === String(selectedAiAuditId));
                if (selectedAudit) this.current_ai_ev_version = selectedAudit.id;
            }
            catch (e) {
                // ignore
            }
            finally {
                this.fetch_loading = false;
            }
          
        },
        async onRevisionApplied({ai_audit_id: aiAuditId} = {}) {
            // 수정안 적용은 서버에서 이미 완료됐다. 사람 평가 복사 없이 AI 평가만 다시 조회한다.
            await this.fetchSubAiEvData(aiAuditId ?? this.current_ai_ev_version);
        },
        async requestAIAssessment(judgmentMode = 'immediate') {
            if(this.sub_id == null){
                const h = this.$createElement;
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('AI 평가 요청 실패'),
                    message: h('div', {}, [
                        h('div', {}, this.$t('평가 데이터가 없어 AI 평가를 요청할 수 없습니다.')),
                        h('div', {}, this.$t('먼저 문항에 답변해주세요.')),
                    ]),
                });
                return;
            }
            
            try {
                this.fetch_loading = true;
                const {cpn, cmp} = this.$route.query;
                const payload = {
                    company_id: cpn, campaign_id: cmp, sub_code: this.sub_code,
                };
                if (this.judgmentModel) payload.judgment_model = this.judgmentModel;
                if (this.judgmentModel || judgmentMode === 'batch') payload.judgment_mode = this.resolveJudgmentMode(judgmentMode);
                const response = await this.$axios.post('/assessments/ai-audit', payload);

                await this.fetchSubAiEvData();
              
                if (response.status === 201) {
                    this.$EventBus.$emit('toast-success', 'AI 평가 요청 완료', response.data.message);
                    return;
                }
                if (response.status === 202) {
                    this.$EventBus.$emit('toast-failure', 'AI 평가 요청 실패', response.data.message);
                }
            }
            catch (error) {
                console.error('AI 평가 요청 중 오류 발생:', error);
                const message = error?.response?.status === 400 && String(getErrorMessage(error, '')).includes('judgment_model')
                    ? '선택한 판정 모델을 사용할 수 없습니다. 드롭다운에서 다시 선택해주세요.'
                    : 'AI 평가 요청 중 오류가 발생했습니다. 개발팀에 문의해주세요.';
                this.$EventBus.$emit('toast-failure', 'AI 평가 요청 실패', message);
            }
            finally {
                this.fetch_loading = false;
            }
            
        },
        // AI 평가 적용은 판정(충족 여부·카테고리)만 옮긴다. AI 근거·총평은 평가자 코멘트
        // (결과보고서에 실림)에 넣지 않는다 — 운영 요청 2026-10-01. AI 근거는 이 패널에서 계속 보인다.
        copyItem(item) {
            const {converted_reason, is_satisfied} = this.get_ai_ev_item(item.ev_info.ev_id);
            item.ev_result.satisfied = is_satisfied;
            item.ev_result.audit_category_code = converted_reason.audit_category_code;
            // 이 판정이 어느 AI 감사 회차(sub_ai_audit.id)에서 왔는지 기록 → 저장 시
            // df_evaluation_results.ai_audit_id 로 이어져 학습 데이터 페어링이 가능해진다.
            item.ev_result.ai_audit_id = this.current_ai_ev_data?.id ?? null;

            this.$emit('handleChange', this.sub_code);
            this.$EventBus.$emit('toast-success', this.$t('AI 평가 적용'), this.$t('단일 항목의 AI 평가를 적용했습니다.'));
        },
        copySub() {
            this.ev_scoring_list.forEach(item => {
                const ai_ev_item = this.get_ai_ev_item(item.ev_info.ev_id);
                // AI 판정(reason)이 없는 항목(valid_map 에는 있으나 미호출)은 건너뛴다.
                if (ai_ev_item?.converted_reason == null) return;
                const {converted_reason, is_satisfied} = ai_ev_item;
                // copyItem 과 동일하게 ev_result.* 에 기록해야 저장 경로(evaluationSave)에 실린다.
                // 기존 top-level manual_satisfied/audit_* 는 v2 저장 로직이 읽지 않는 legacy 잔재라
                // 지금까지 "문항 전체 적용"이 사실상 저장되지 않았다. 여기서 통일한다.
                item.ev_result.satisfied = is_satisfied;
                item.ev_result.audit_category_code = converted_reason.audit_category_code;
                item.ev_result.ai_audit_id = this.current_ai_ev_data?.id ?? null;
            });
            this.$emit('handleChange', this.sub_code);
            this.$EventBus.$emit('toast-success', this.$t('AI 평가 적용'), this.$t('문항의 AI 평가를 적용했습니다.'));
        },
        openDetailAIModal() {
            /**
             * api data 정해지면 eventbus로 모달 오픈하는 방향으로 수정
             * @param data
             */
            const {cpn, cmp, cpnm, pc} = this.$route.query;
            this.$EventBus.$emit('ai-assessment-detail-modal:open', {
                project_code: pc,
                company_id: cpn,
                campaign_id: cmp,
                company_name: cpnm,
                vendor_code: null,
                sub_id: this.sub_id,
                label: this.sub_label,
                sub_code: this.sub_code,
                current_ai_ev_version: this.current_ai_ev_version,
            });
        },
        openQnaModal(item) {
            if (!this.current_ai_ev_data?.id) {
                this.$EventBus.$emit('single-toast-on', {
                    title: this.$t('AI 평가 Q&A'),
                    content: this.$t('Q&A 대상 AI 평가를 찾을 수 없습니다.'),
                    variant: 'warning',
                });
                return;
            }
            this.selectedQnaEvId = item.ev_info?.ev_id ?? null;
            this.selectedQnaEvName = item.ev_info?.name || '';
            this.$bvModal.show(this.qnaModalId);
        },
        copyAuditBasis(text) {
            if (text) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$EventBus.$emit('single-toast-on', {
                        title: this.$t('AI 평가 근거 복사'),
                        content: this.$t('AI 평가 근거을 클립보드에 복사했습니다.'),
                        variant: 'success',
                    });
                }).catch(() => {
                    this.$EventBus.$emit('single-toast-on', {
                        title: this.$t('복사 실패'),
                        content: this.$t('AI 평가 근거 복사에 실패했습니다.'),
                        variant: 'error',
                    });
                });
            } else {
                this.$EventBus.$emit('single-toast-on', {
                    title: this.$t('복사 실패'),
                    content: this.$t('AI 평가 근거가 없습니다.'),
                    variant: 'warning',
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>

.audit-desc {
    width: 100%;
    font-size: 0.95rem;
    color: #6c757d;
    margin-bottom: 5px;
}

.audit-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;
    margin-top: 10px;
    margin-right: 12px;

    .audit-category {
    }

    .audit-comment {
        resize: none;
    }
}

.ai-ev-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 17px;
    font-size: 0.95rem;
    color: #6c757d;
    height: 60px;

    .versions {
        display: flex;
        align-items: center;
        gap: 8px;

        .version-select {
            width: 100px;
        }
    }
}
.no-ai-audit-data-container{
    margin-top: 9px;
    width: 100%;
    padding: 0 12px;
    margin-bottom: 2px;
    .no-ai-audit_title-wrapper {
        display: flex;
        align-items: center;
        font-size: 1rem;
        color: #6c757d;
        width: 100%;
        .no-ai-audit_title {
            border: 1px solid #ced4da;
            padding: 4px 8px;
            border-radius: 4px;
            width: 100%;
            
            font-weight: bold;
            margin-left: 0;
        }
    }
    .no-ai-audit_desc {
        margin-left: 24px;
        border: 1px solid #ced4da;
        padding: 8px;
        height: 142px;
        border-radius: 4px;
        font-size: 0.95rem;
        color: #6c757d;
        margin-top: 12px;
        white-space: pre-wrap;
    }
}

.ai-report-icon {
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        color: #007bff !important;
        transform: scale(1.1);
    }
}

</style>
